import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { Toaster } from "sonner";
import { usePageTracker } from "./hooks/usePageTracker";

// Pages
import ComingSoon from "./pages/ComingSoon";
import LandingPage from "./pages/LandingPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import TreadDepthPage from "./pages/TreadDepthPage";
import CityLandingPage from "./pages/CityLandingPage";
import ForShopsPage from "./pages/ForShopsPage";
import GuestQuoteRequest from "./pages/GuestQuoteRequest";
import TokenQuoteView from "./pages/TokenQuoteView";
import SoftRedirect from "./components/SoftRedirect";
import CustomerAuth from "./pages/CustomerAuth";
import ShopAuth from "./pages/ShopAuth";
import AdminAuth from "./pages/AdminAuth";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerAccount from "./pages/CustomerAccount";
import ShopDashboard from "./pages/ShopDashboard";
import ShopAccount from "./pages/ShopAccount";
import ShopPublicProfile from "./pages/ShopPublicProfile";
import AdminDashboard from "./pages/AdminDashboard";
import FranchiseDashboard from "./pages/FranchiseDashboard";
import QuoteRequest from "./pages/QuoteRequest";
import PaymentSuccess from "./pages/PaymentSuccess";
import UrgentPaymentSuccess from "./pages/UrgentPaymentSuccess";

// Components
import AIChatWidget from "./components/AIChatWidget";

// Context
const AuthContext = createContext(null);
const ThemeContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Theme Provider
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem("token"));
  const [impersonating, setImpersonating] = useState(() => sessionStorage.getItem("impersonating") === "true");
  const [impersonatedName, setImpersonatedName] = useState(() => sessionStorage.getItem("impersonatedName") || null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      setUserType(response.data.user_type);
    } catch (error) {
      // If the failed token was an impersonation token, fall back to the
      // stashed admin token rather than full-logout (which would also wipe
      // the admin's session and dump them at "/").
      const stashedAdminToken = sessionStorage.getItem("impersonator_token");
      const wasImpersonating = sessionStorage.getItem("impersonating") === "true";
      if (wasImpersonating && stashedAdminToken) {
        sessionStorage.setItem("token", stashedAdminToken);
        sessionStorage.removeItem("impersonator_token");
        sessionStorage.removeItem("impersonating");
        sessionStorage.removeItem("impersonatedName");
        setToken(stashedAdminToken);
        setImpersonating(false);
        setImpersonatedName(null);
        // Will trigger re-fetch via the [token] dep on the next render.
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = (tokenData, userData, type) => {
    sessionStorage.setItem("token", tokenData);
    setToken(tokenData);
    setUser(userData);
    setUserType(type);
  };

  const logout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("impersonator_token");
    sessionStorage.removeItem("impersonating");
    sessionStorage.removeItem("impersonatedName");
    setToken(null);
    setUser(null);
    setUserType(null);
    setImpersonating(false);
    setImpersonatedName(null);
  };

  // Start impersonation: stash admin token, swap to short-lived impersonation token
  const startImpersonation = (impToken, impUser, impType, displayName) => {
    // Preserve the ORIGINAL admin token across nested impersonation hops
    // (e.g. admin → franchise owner → individual store). Without this guard
    // a second startImpersonation() call would overwrite the stash with the
    // owner impersonation token and `stopImpersonation()` would restore the
    // wrong identity.
    if (!sessionStorage.getItem("impersonator_token")) {
      const adminToken = sessionStorage.getItem("token");
      if (adminToken) {
        sessionStorage.setItem("impersonator_token", adminToken);
      }
    }
    sessionStorage.setItem("token", impToken);
    sessionStorage.setItem("impersonating", "true");
    sessionStorage.setItem("impersonatedName", displayName || "");
    setToken(impToken);
    setUser(impUser);
    setUserType(impType);
    setImpersonating(true);
    setImpersonatedName(displayName || "");
  };

  // Exit impersonation: restore admin token from stash
  const stopImpersonation = () => {
    const adminToken = sessionStorage.getItem("impersonator_token");
    sessionStorage.removeItem("impersonator_token");
    sessionStorage.removeItem("impersonating");
    sessionStorage.removeItem("impersonatedName");
    setImpersonating(false);
    setImpersonatedName(null);
    if (adminToken) {
      sessionStorage.setItem("token", adminToken);
      setToken(adminToken);
      // fetchUser will run via the token-change effect and rehydrate admin user
    } else {
      // No stashed admin token — full logout
      logout();
    }
  };

  return (
    <AuthContext.Provider value={{ user, userType, token, loading, impersonating, impersonatedName, login, logout, fetchUser, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, userType, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedTypes && !allowedTypes.includes(userType)) {
    // Send each user type to the right place rather than dumping at "/".
    if (userType === "franchise_owner") return <Navigate to="/franchise/dashboard" replace />;
    if (userType === "shop") return <Navigate to="/shop/dashboard" replace />;
    if (userType === "customer") return <Navigate to="/customer/dashboard" replace />;
    if (userType === "admin") return <Navigate to="/nedhaavu/dashboard" replace />;
    return <Navigate to="/" replace />;
  }

  return children;
};

function DynamicHomepage() {
  const [mode, setMode] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/homepage-mode`)
      .then(res => setMode(res.data.homepage_mode))
      .catch(() => setMode("coming_soon"));
  }, []);
  if (!mode) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  return mode === "home" ? <LandingPage /> : <ComingSoon />;
}

function PageTracker() {
  const { userType } = useAuth();
  usePageTracker(userType || "anonymous");
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <PageTracker />
            <div className="min-h-screen bg-background">
              <Routes>
                {/* Dynamic Homepage — controlled by admin toggle */}
                <Route path="/" element={<DynamicHomepage />} />
                
                {/* Original Landing Page - accessible via /home2 */}
                <Route path="/home2" element={<LandingPage />} />

                {/* Public marketing pages (Feb 2026) — SEO landing */}
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/tread-depth-nz" element={<TreadDepthPage />} />
                <Route path="/for-shops" element={<ForShopsPage />} />
                {/* Legacy alias URLs — SoftRedirect emits explicit
                    noindex + canonical + meta-refresh + hard
                    window.location.replace. HTTP 301 is not currently
                    supported at Emergent's hosting layer (see PRD note);
                    when it is, delete SoftRedirect and add the rules
                    server-side. */}
                <Route path="/how-private-tyre-quotes-work" element={<SoftRedirect to="/how-it-works" />} />
                <Route path="/wet-roads-tyre-tread" element={<SoftRedirect to="/tread-depth-nz" />} />
                {/* City landings — explicit routes because React Router v6
                    does not treat `:city` inside a hyphenated segment as a
                    dynamic param. Passing citySlug as prop is deterministic. */}
                <Route path="/tyre-quotes-auckland" element={<CityLandingPage citySlug="auckland" />} />
                <Route path="/tyre-quotes-wellington" element={<CityLandingPage citySlug="wellington" />} />
                <Route path="/tyre-quotes-christchurch" element={<CityLandingPage citySlug="christchurch" />} />
                {/* Legacy dynamic pattern kept as a soft fallback (no-op in RRv6 but harmless) */}
                <Route path="/tyre-quotes-:city" element={<CityLandingPage />} />

                {/* Guest quote request — public, no login required (Feb 2026) */}
                <Route path="/quote-request" element={<GuestQuoteRequest />} />
                {/* Magic-link comparison page — one token per request, reused forever */}
                <Route path="/r/:token" element={<TokenQuoteView />} />
                
                {/* Auth Routes */}
                <Route path="/customer/auth" element={<CustomerAuth />} />
                <Route path="/shop/auth" element={<ShopAuth />} />
                <Route path="/nedhaavu/auth" element={<AdminAuth />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/urgent-payment-success" element={
                  <ProtectedRoute allowedTypes={["customer"]}>
                    <UrgentPaymentSuccess />
                  </ProtectedRoute>
                } />

                {/* Customer Routes */}
                <Route path="/customer/dashboard" element={
                  <ProtectedRoute allowedTypes={["customer"]}>
                    <CustomerDashboard />
                </ProtectedRoute>
              } />
              <Route path="/customer/account" element={
                <ProtectedRoute allowedTypes={["customer"]}>
                  <CustomerAccount />
                </ProtectedRoute>
              } />
              <Route path="/customer/quote-request" element={
                <ProtectedRoute allowedTypes={["customer"]}>
                  <QuoteRequest />
                </ProtectedRoute>
              } />

              {/* Shop Routes */}
              <Route path="/shop/dashboard" element={
                <ProtectedRoute allowedTypes={["shop"]}>
                  <ShopDashboard />
                </ProtectedRoute>
              } />
              <Route path="/shop/account" element={
                <ProtectedRoute allowedTypes={["shop"]}>
                  <ShopAccount />
                </ProtectedRoute>
              } />
              <Route path="/shop/profile/:shop_id" element={<ShopPublicProfile />} />

              {/* Admin Routes */}
              <Route path="/nedhaavu/dashboard" element={
                <ProtectedRoute allowedTypes={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />

              {/* Franchise / Multi-location Owner Routes */}
              <Route path="/franchise/dashboard" element={
                <ProtectedRoute allowedTypes={["franchise_owner"]}>
                  <FranchiseDashboard />
                </ProtectedRoute>
              } />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {/* AI Chat Widget - Available on all pages for logged-in users */}
            <AIChatWidget />
          </div>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
